import { LegalTip } from './ui'
import type { WizardState } from './types'

const REQUIRED: Record<number, { key: keyof WizardState; label: string }[]> = {
  0: [
    { key: 'lease_display_type', label: 'Boligtype' },
    { key: 'area_m2', label: 'Areal' },
    { key: 'room_count', label: 'Antal værelser' },
    { key: 'monthly_rent_dkk', label: 'Månedlig husleje' },
    { key: 'start_date', label: 'Indflytningsdato' },
  ],
  1: [
    { key: 'heating_billing_type', label: 'Varme afregning' },
    { key: 'water_billing_type', label: 'Vand afregning' },
    { key: 'el_billing_type', label: 'El afregning' },
  ],
  2: [
    { key: 'deposit_dkk', label: 'Depositum' },
    { key: 'prepaid_rent_dkk', label: 'Forudbetalt leje' },
  ],
  7: [
    { key: 'renter_id', label: 'Lejer valgt' },
  ],
}

// Tips are conditional on state values — only show relevant ones
function computeTips(step: number, state: WizardState): { title: string; text: string }[] {
  const tips: { title: string; text: string }[] = []

  if (step === 0) {
    if (state.lease_display_type) {
      tips.push({ title: 'Boligtype', text: 'Boligtypen påvirker hvilke regler der gælder — f.eks. om fri leje er tilladt jf. lejelovens §54.' })
    }
    if (state.free_rent) {
      tips.push({ title: 'Fri leje §54', text: 'Kræver at ét af §54\'s tre grundlag er opfyldt. Dokumentér altid grundlaget skriftligt for at undgå tvist.' })
    } else if (state.monthly_rent_dkk) {
      tips.push({ title: 'Huslejeniveau', text: 'Huslejen skal fastsættes efter lejelovens §47 (det lejedes værdi) med mindre fri leje er aftalt.' })
    }
    if (state.nettoprisindeks) {
      tips.push({ title: 'Nettoprisindeks', text: 'Regulering sker automatisk én gang årligt. Basis-år afgør udgangspunktet for reguleringen.' })
    }
    if (state.is_fixed_term) {
      tips.push({ title: 'Tidsbegrænset lejemål', text: 'Tidsbegrænsede lejeforhold kræver saglig begrundelse. Uden godkendt begrundelse behandles aftalen som uopsigelig.' })
    }
  }

  if (step === 1) {
    if (state.heating_billing_type === 'aconto') {
      tips.push({ title: 'A conto varme', text: 'Beløbet skal svare til forventet forbrug. Årsregnskab skal udsendes senest 4 måneder efter regnskabsårets afslutning.' })
    }
    if (state.el_billing_type === 'aconto') {
      tips.push({ title: 'El a conto', text: 'Kun lovligt hvis der er individuelle forbrugsmålere og der udføres et årligt forbrugsregnskab.' })
    }
    if (state.beboerrepraesentation) {
      tips.push({ title: 'Beboerrepræsentation', text: 'Beløbet til beboerrepræsentationen er reguleret og skal oplyses tydeligt i kontrakten.' })
    }
    if (state.antenna_type === 'faellesantenne') {
      tips.push({ title: 'Fællesantenne', text: 'Udgifter til fællesantenne skal dokumenteres via et varmeregnskab eller tilsvarende opgørelse.' })
    }
  }

  if (step === 2) {
    tips.push({ title: 'Depositum', text: 'Må ikke overstige 3 måneders husleje (ekskl. forbrug), jf. lejelovens §34.' })
    tips.push({ title: 'Forudbetalt leje', text: 'Må ikke overstige 3 måneders husleje. Modregnes i lejeperiodens slutning.' })
  }

  if (step === 3) {
    if (state.inventory_notes) {
      tips.push({ title: 'Inventar', text: 'Beskriv inventar detaljeret — dette skaber klare forventninger og letter fraflytningssyn.' })
    }
    if (state.interior_maintenance === 'lejer') {
      tips.push({ title: 'Indvendig vedligeholdelse', text: 'Hvis lejer er ansvarlig, skal vedligeholdelsespligten beskrives præcist i §8.' })
    }
  }

  if (step === 4) {
    if (state.inspection_date) {
      tips.push({ title: 'Indflytningssyn', text: 'Et gennemført indflytningssyn styrker din retsstilling ved fraflytning markant.' })
    } else {
      tips.push({ title: 'Indflytningssyn', text: 'Anbefales altid. Uden syn kan udlejer ikke kræve istandsættelse ved fraflytning.' })
    }
    tips.push({ title: 'Husorden', text: 'Husorden skal oplyses inden lejeaftalens indgåelse for at være bindende.' })
    if (state.pets_allowed) {
      tips.push({ title: 'Husdyr tilladt', text: 'Overvej at præcisere vilkår for husdyr i §11 for at undgå fortolkningstvivl.' })
    }
  }

  if (step === 5) {
    tips.push({ title: '§11 Særlige vilkår', text: 'Vilkår der stiller lejer dårligere end lejeloven er ugyldige — medmindre fri leje er aftalt jf. §54.' })
  }

  if (step === 6) {
    tips.push({ title: 'Udlejer', text: 'CVR-nummeret bruges til officiel identifikation. Kontaktinfo vises på lejekontrakten.' })
  }

  if (step === 7) {
    tips.push({ title: 'Lejer', text: 'Lejeren modtager en email med link til digital signering når kontrakten sendes.' })
  }

  return tips
}

function isFilled(state: WizardState, key: keyof WizardState): boolean {
  const v = state[key]
  if (v === null || v === undefined || v === '' || v === false) return false
  if (Array.isArray(v)) return v.length > 0
  return true
}

interface Props {
  state: WizardState
  currentStep: number
  totalSteps: number
  doneSections: Set<number>
}

export function KontraktAssistent({ state, currentStep, totalSteps, doneSections }: Props) {
  const required = REQUIRED[currentStep] ?? []
  const tips = computeTips(currentStep, state)
  const missing = required.filter(f => !isFilled(state, f.key))
  const doneCount = doneSections.size
  const pct = Math.round((doneCount / totalSteps) * 100)

  return (
    <div style={{
      width: 280, background: 'white', borderLeft: '1px solid #e5e7eb',
      display: 'flex', flexDirection: 'column', overflowY: 'auto',
      position: 'sticky', top: 0, alignSelf: 'flex-start',
      maxHeight: 'calc(100vh - 56px)',
      padding: '20px 16px', gap: 0, flexShrink: 0,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 16 }}>
        Kontraktassistent
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Samlet fremgang
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{doneCount}/{totalSteps} trin</span>
        </div>
        <div style={{ height: 5, background: '#f3f4f6', borderRadius: 3 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: '#10b981', borderRadius: 3, transition: 'width .3s' }} />
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{pct}% udfyldt</div>
      </div>

      {/* Required fields */}
      {required.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Påkrævede felter — dette trin
          </div>
          {missing.length > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 10px', marginBottom: 10, fontSize: 12, color: '#92400e' }}>
              ⚠ {missing.length} {missing.length === 1 ? 'felt mangler' : 'felter mangler'}
            </div>
          )}
          {required.map(f => {
            const filled = isFilled(state, f.key)
            return (
              <div key={String(f.key)} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: filled ? '#10b981' : '#f59e0b',
                }} />
                <span style={{ fontSize: 13, color: filled ? '#059669' : '#374151' }}>{f.label}</span>
              </div>
            )
          })}
        </div>
      )}

      {/* Legal tips — only shown when relevant */}
      {tips.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Juridisk vejledning
          </div>
          {tips.map((t, i) => <LegalTip key={i} title={t.title} text={t.text} />)}
        </div>
      )}

      {tips.length === 0 && required.length === 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>
          Udfyld felterne til venstre for at se relevant vejledning.
        </p>
      )}
    </div>
  )
}
